import React, { Fragment } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Box, Modal, Button, IconButton, CircularProgress } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EngineeringIcon from '@mui/icons-material/Engineering';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

function ModalEquipoInstalacionComponent({ open, handleClose, dataInstalacion }) {
    const equiposInstalacion = useSelector(store => store.ModuloConsulta?.equiposInstalacion || []);
    const [cargando, setCargando] = React.useState(false);

    const infoGeneral = dataInstalacion?.informaciongeneralinstalacioncliente || {};
    const tipoAnexo = dataInstalacion?.tipoanexo?.descripcion || 'INSTALACIÓN';
    const alias = infoGeneral.alias || 'S/N';

    const domicilioCompleto = [
        infoGeneral.calle,
        infoGeneral.numerointerior ? `Int. ${infoGeneral.numerointerior}` : null,
        infoGeneral.numeroexterior ? `Ext. ${infoGeneral.numeroexterior}` : null,
        infoGeneral.colonia ? `Col. ${infoGeneral.colonia}` : null,
        infoGeneral.localidad,
        infoGeneral.municipio,
        infoGeneral.estado,
        infoGeneral.codigopostal ? `C.P. ${infoGeneral.codigopostal}` : null
    ].filter(Boolean).join(', ');

    const styleModal = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '85%',
        maxWidth: 950,
        maxHeight: '90vh',
        bgcolor: '#ffffff',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        borderRadius: '16px',
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: '#8ABE3E'
            },
            secondary: {
                main: '#DC3545'
            }
        },
        typography: {
            fontFamily: 'Montserrat, sans-serif'
        }
    });

    return (
        <ThemeProvider theme={theme}>
            <Modal open={open} onClose={handleClose} aria-labelledby="modal-equipo-title">
                <Box sx={styleModal}>
                    {/* Header */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                        color: '#ffffff',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '3px solid #8ABE3E'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                backgroundColor: 'rgba(138, 190, 62, 0.2)',
                                p: 1,
                                borderRadius: '12px',
                                display: 'flex'
                            }}>
                                <EngineeringIcon sx={{ color: '#8ABE3E', fontSize: 30 }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                                    {tipoAnexo}
                                </Typography>
                                <Typography id="modal-equipo-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {alias.toUpperCase()}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleClose} sx={{ color: '#94A3B8', '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content Body */}
                    <Box sx={{ p: 3, overflowY: 'auto', flex: 1, backgroundColor: '#F8FAFC' }}>
                        {/* Ubicación Summary Card */}
                        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#ffffff' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <LocationOnIcon sx={{ color: '#8ABE3E', mt: 0.3 }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                        Ubicación de la Instalación
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                                        {domicilioCompleto || 'Sin información de domicilio'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Responsables Section */}
                        {infoGeneral?.responsablesdeinstalacioncliente?.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon sx={{ color: '#8ABE3E', fontSize: 20 }} /> Responsables de Instalación
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {infoGeneral.responsablesdeinstalacioncliente.map((resp, idx) => (
                                        <Paper key={idx} elevation={0} sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #E2E8F0', flex: '1 1 250px', backgroundColor: '#ffffff' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                                                {resp.nombreresponsable?.toUpperCase()}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#64748B' }}>
                                                Tel: {resp.telefono || 'N/A'} | {resp.correoelectronico || 'N/A'}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Equipos Table Section */}
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                                    Equipos y Maquinaria Registrados
                                </Typography>
                                <Chip
                                    label={`${equiposInstalacion.length} Equipo(s)`}
                                    size="small"
                                    sx={{ backgroundColor: '#E2E8F0', fontWeight: 600, color: '#334155' }}
                                />
                            </Box>

                            {equiposInstalacion.length === 0 ? (
                                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '12px', border: '1px dashed #CBD5E1', backgroundColor: '#ffffff' }}>
                                    <EngineeringIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 1 }} />
                                    <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600 }}>
                                        No hay equipos registrados
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#94A3B8', mt: 0.5 }}>
                                        Esta instalación aún no cuenta con información de equipos o maquinaria registrada en el sistema.
                                    </Typography>
                                </Paper>
                            ) : (
                                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                                    <Table sx={{ minWidth: 650 }}>
                                        <TableHead sx={{ backgroundColor: '#1E293B' }}>
                                            <TableRow>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Sección</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Elemento / Equipo</TableCell>
                                                <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 700 }}>Cantidad</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Descripción / Especificaciones</TableCell>
                                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Observaciones / Otro</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {equiposInstalacion.map((item, index) => {
                                                const nombreSeccion = item?.seccion?.nombre || item?.seccion?.nombreseccion || 'General';
                                                const nombreElemento = item?.elemento?.nombreelemento || item?.elemento?.nombre || 'Elemento';
                                                return (
                                                    <TableRow
                                                        key={item.iddataelementocliente || index}
                                                        sx={{
                                                            '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' },
                                                            '&:hover': { backgroundColor: '#F1F5F9' },
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                    >
                                                        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
                                                            <Chip label={nombreSeccion} size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#0F172A' }}>
                                                            {nombreElemento}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip label={item.cantidad ?? 1} size="small" color="primary" sx={{ color: '#ffffff', fontWeight: 700 }} />
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#475569' }}>
                                                            {item.descripcion || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#475569' }}>
                                                            {item.otro || '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Box>

                    {/* Modal Actions Footer */}
                    <Box sx={{ p: 2, px: 3, borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#ffffff' }}>
                        <Button variant="contained" color="secondary" onClick={handleClose} sx={{ px: 4, py: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
                            Cerrar
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </ThemeProvider>
    );
}

export default ModalEquipoInstalacionComponent;
